/**
 * Backend-aligned rule definitions for the promotion rule builder.
 * Keeps the same UI structure and functionality as data.js _rulesStructure,
 * but maps attribute names to backend expectations so create/update payloads work seamlessly.
 *
 * Usage: PromotionRule.jsx (and IfRuleView / ThenRuleView) import rulesStructure from here.
 */

import { cloneDeep } from "lodash";
import { _rulesStructure } from "./data";
import { FRONTEND_TO_BACKEND_ATTRIBUTES } from "./backendRuleAttributes";

function mapAttributesInRule(rule) {
  if (!rule || typeof rule !== "object") return rule;
  const out = { ...rule };
  if (out.attributes && FRONTEND_TO_BACKEND_ATTRIBUTES[out.attributes]) {
    out.attributes = FRONTEND_TO_BACKEND_ATTRIBUTES[out.attributes];
  }
  if (out.groupRule) {
    out.groupRule = mapAttributesInRule(out.groupRule);
  }
  return out;
}

function mapRulesInArray(arr) {
  if (!Array.isArray(arr)) return arr;
  return arr.map(mapAttributesInRule);
}

function mapCriteriaBlock(block) {
  if (!block || typeof block !== "object") return block;
  const out = { ...block };
  if (Array.isArray(out.ifRules)) out.ifRules = mapRulesInArray(out.ifRules);
  if (Array.isArray(out.thenRules)) out.thenRules = mapRulesInArray(out.thenRules);
  return out;
}

function mapRuleType(typeNode) {
  if (!typeNode || typeof typeNode !== "object") return typeNode;
  const out = {};
  for (const key of Object.keys(typeNode)) {
    const val = typeNode[key];
    if (Array.isArray(val) && (key === "ifRules" || key === "thenRules")) {
      out[key] = mapRulesInArray(val);
    } else if (val && typeof val === "object" && !Array.isArray(val) && typeof val.ifRules !== "undefined") {
      out[key] = mapCriteriaBlock(val);
    } else {
      out[key] = val;
    }
  }
  return out;
}

/**
 * Rules structure with backend-aligned attribute names.
 * Same shape as _rulesStructure; use this as the single source for the rule builder.
 */
export const rulesStructure = (() => {
  const raw = cloneDeep(_rulesStructure);
  const out = {};
  for (const ruleType of Object.keys(raw)) {
    out[ruleType] = mapRuleType(raw[ruleType]);
  }
  return out;
})();

export default rulesStructure;
